import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { formatINR } from '../utils/priceFormatter';
import SEO from '../components/SEO';
import ProductModal from '../components/ProductModal';

// Import product images
import shoe1 from '../components/images/shoe1.png';
import shoe2 from '../components/images/shoe2.png';
import shoe5 from '../components/images/shoe5.png';
import shoe6 from '../components/images/shoe6.png';
import shoe7 from '../components/images/shoe7.png';
import shoe8 from '../components/images/shoe8.png';
import shoe9 from '../components/images/shoe9.png';
import sh1 from '../components/images/sh1.png';
import sh2 from '../components/images/sh2.png';
import sh3 from '../components/images/sh3.png';

gsap.registerPlugin(ScrollTrigger);

// Extended product data with more details for filtering (Prices in INR)
const allProducts = [
  {
    id: 1,
    name: "Air Max 270",
    category: "Lifestyle",
    price: 12450, // 150 USD * 83
    image: shoe1,
    description: "Maximum air, maximum style. The Nike Air Max 270 delivers visible Air cushioning from heel to toe.",
    badge: "BESTSELLER",
    colors: ["Black", "White", "Red"],
    sizes: [7, 8, 9, 10, 11, 12],
    rating: 4.8,
    reviews: 1250
  },
  {
    id: 2,
    name: "Air Force 1",
    category: "Classic",
    price: 9130, // 110 USD * 83
    image: shoe2,
    description: "Timeless basketball icon with premium leather and classic styling that never goes out of fashion.",
    badge: "CLASSIC",
    colors: ["White", "Black", "Navy"],
    sizes: [6, 7, 8, 9, 10, 11, 12, 13],
    rating: 4.9,
    reviews: 2100
  },
  {
    id: 3,
    name: "React Infinity Run",
    category: "Running",
    price: 13280, // 160 USD * 83
    image: shoe5,
    description: "Engineered for comfort and support during long-distance runs with React foam technology.",
    badge: "NEW",
    colors: ["Blue", "Black", "Gray"],
    sizes: [7, 8, 9, 10, 11, 12],
    rating: 4.6,
    reviews: 890
  },
  {
    id: 4,
    name: "ZoomX Vaporfly",
    category: "Performance",
    price: 20750, // 250 USD * 83
    image: shoe6,
    description: "Built for speed with ZoomX foam and carbon fiber plate for maximum energy return.",
    badge: "PREMIUM",
    colors: ["Pink", "Green", "Orange"],
    sizes: [7, 8, 9, 10, 11, 12],
    rating: 4.7,
    reviews: 567
  },
  {
    id: 5,
    name: "Blazer Mid",
    category: "Lifestyle",
    price: 8300, // 100 USD * 83
    image: shoe7,
    description: "Retro basketball style meets modern comfort for everyday casual wear.",
    badge: "TRENDING",
    colors: ["White", "Black", "Green"],
    sizes: [6, 7, 8, 9, 10, 11, 12],
    rating: 4.5,
    reviews: 743
  },
  {
    id: 6,
    name: "Dunk Low",
    category: "Street",
    price: 9130, // 110 USD * 83
    image: shoe8,
    description: "Street style icon with premium materials and classic basketball-inspired design.",
    badge: "LIMITED",
    colors: ["White", "Black", "Red", "Blue"],
    sizes: [6, 7, 8, 9, 10, 11, 12, 13],
    rating: 4.8,
    reviews: 1456
  },
  {
    id: 7,
    name: "Air Jordan 1",
    category: "Basketball",
    price: 14110, // 170 USD * 83
    image: shoe9,
    description: "Legendary basketball heritage with iconic silhouette and premium construction.",
    badge: "ICONIC",
    colors: ["Red", "Black", "White"],
    sizes: [7, 8, 9, 10, 11, 12, 13],
    rating: 4.9,
    reviews: 1890
  },
  {
    id: 8,
    name: "Air Max 90",
    category: "Lifestyle",
    price: 9960, // 120 USD * 83
    image: sh1,
    description: "Maximum air for all-day comfort with visible Air cushioning and retro styling.",
    badge: "POPULAR",
    colors: ["White", "Gray", "Black"],
    sizes: [6, 7, 8, 9, 10, 11, 12],
    rating: 4.7,
    reviews: 1123
  },
  {
    id: 9,
    name: "Air Huarache",
    category: "Lifestyle",
    price: 10790, // 130 USD * 83
    image: sh2,
    description: "Iconic design with a snug fit and unique aesthetic that stands out from the crowd.",
    badge: "RETRO",
    colors: ["Black", "White", "Blue"],
    sizes: [7, 8, 9, 10, 11, 12],
    rating: 4.4,
    reviews: 654
  },
  {
    id: 10,
    name: "Free RN",
    category: "Running",
    price: 7470, // 90 USD * 83
    image: sh3,
    description: "Natural feel for a more connected run with flexible sole and lightweight construction.",
    badge: "BUDGET",
    colors: ["Gray", "Black", "Blue"],
    sizes: [6, 7, 8, 9, 10, 11, 12, 13],
    rating: 4.3,
    reviews: 456
  },
  {
    id: 11,
    name: "Air Max 270",
    category: "Lifestyle",
    price: 12450, // 150 USD * 83
    image: shoe1,
    description: "Maximum air, maximum style. The Nike Air Max 270 delivers visible Air cushioning from heel to toe.",
    badge: "BESTSELLER",
    colors: ["Black", "White", "Red"],
    sizes: [7, 8, 9, 10, 11, 12],
    rating: 4.8,
    reviews: 1250
  },
  {
    id: 12,
    name: "Air Force 1",
    category: "Classic",
    price: 9130, // 110 USD * 83
    image: shoe2,
    description: "Timeless basketball icon with premium leather and classic styling that never goes out of fashion.",
    badge: "CLASSIC",
    colors: ["White", "Black", "Navy"],
    sizes: [6, 7, 8, 9, 10, 11, 12, 13],
    rating: 4.9,
    reviews: 2100
  }
];

const categories = ["All", "Lifestyle", "Running"];
const sortOptions = [
  { value: "featured", label: "Featured" },
  { value: "price-low", label: "Price: Low to High" },
  { value: "price-high", label: "Price: High to Low" },
  { value: "name", label: "Name A-Z" },
  { value: "rating", label: "Highest Rated" }
];

const Store = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState("featured");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [productsInCart, setProductsInCart] = useState(new Set());
  const navigate = useNavigate();
  const { cartItems } = useCart();
  const { toggleWishlistItem, isItemInWishlist } = useWishlist();
  const [isFilterVisible, setIsFilterVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const filtersRef = useRef();
  const productsRef = useRef();

  useEffect(() => {
    // Entrance animations
    const tl = gsap.timeline();
    
    tl.fromTo(filtersRef.current,
      { y: -20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" }
    )
    .fromTo(productsRef.current,
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" },
      "-=0.4"
    );
  }, []);

  // Handle scroll-based filter visibility
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollThreshold = 100; // Minimum scroll distance to trigger hide/show
      
      // Only update if scroll distance is significant enough
      if (Math.abs(currentScrollY - lastScrollY) < scrollThreshold) {
        return;
      }
      
      // Show filters when scrolling up or at top
      if (currentScrollY < lastScrollY || currentScrollY < 200) {
        setIsFilterVisible(true);
      } 
      // Hide filters when scrolling down (but not at the very top)
      else if (currentScrollY > lastScrollY && currentScrollY > 200) {
        setIsFilterVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    // Throttle scroll events for better performance
    let timeoutId = null;
    const throttledHandleScroll = () => {
      if (timeoutId === null) {
        timeoutId = setTimeout(() => {
          handleScroll();
          timeoutId = null;
        }, 100);
      }
    };

    window.addEventListener('scroll', throttledHandleScroll);
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [lastScrollY]);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let filtered = allProducts.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === "All" || product.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });

    // Sort products
    switch (sortBy) {
      case "price-low":
        filtered.sort((a, b) => a.price - b.price);
        break;
      case "price-high":
        filtered.sort((a, b) => b.price - a.price);
        break;
      case "name":
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "rating":
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      default:
        // Keep original order for featured
        break;
    }

    return filtered;
  }, [searchTerm, selectedCategory, sortBy]);

  const openModal = (product, focusOnSize = false) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
    
    // If focusOnSize is true, we'll pass this info to ProductModal
    if (focusOnSize) {
      setSelectedProduct({ ...product, focusOnSize: true });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const handleCartAction = (product) => {
    if (productsInCart.has(product.id)) {
      // Product is in cart, go to cart page
      navigate('/cart');
    } else {
      // Product not in cart, open modal for adding
      openModal(product, true);
    }
  };

  // Track products in cart
  useEffect(() => {
    const productIds = new Set(cartItems.map(item => item.id));
    setProductsInCart(productIds);
  }, [cartItems]);

  const handleAddToCart = (cartItem) => {
    console.log('Added to cart:', cartItem);
    // Here you would typically add to cart state or send to backend
    alert(`Added ${cartItem.product.name} (Size: ${cartItem.size}, Color: ${cartItem.color}, Qty: ${cartItem.quantity}) to cart!`);
  };

  const handleWishlistToggle = async (product, event) => {
    event.stopPropagation();
    await toggleWishlistItem(product);
  };



  const storeStructuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Nike Store - Athletic Footwear & Sportswear Collection",
    "description": "Shop Nike's complete collection of athletic footwear, sportswear, and accessories. Find Air Max, Jordan, React, and more with advanced filtering and search.",
    "url": "https://nike3d.com/store",
    "mainEntity": {
      "@type": "ItemList",
      "name": "Nike Product Collection",
      "description": "Complete Nike athletic footwear and sportswear collection",
      "numberOfItems": allProducts.length,
      "itemListElement": filteredProducts.slice(0, 5).map((product, index) => ({
        "@type": "Product",
        "position": index + 1,
        "name": product.name,
        "category": product.category,
        "brand": "Nike",
        "offers": {
          "@type": "Offer",
          "price": product.price,
          "priceCurrency": "INR",
          "availability": "https://schema.org/InStock"
        }
      }))
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://nike3d.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Store",
          "item": "https://nike3d.com/store"
        }
      ]
    }
  };

  return (
    <StoreContainer>
      <SEO
        title="Nike Store - Athletic Footwear & Sportswear Collection | Nike 3D"
        description="Shop Nike's complete collection of athletic footwear, sportswear, and accessories. Find Air Max, Jordan, React, and more with advanced filtering and search. Free shipping on orders over $100."
        keywords="Nike store, athletic shoes, sportswear, Air Max, Jordan, React, running shoes, basketball shoes, lifestyle sneakers, Nike collection, sports apparel, athletic gear"
        canonicalUrl="https://nike3d.com/store"
        structuredData={storeStructuredData}
      />
      {/* Filters Section */}
      <FiltersSection ref={filtersRef} isVisible={isFilterVisible}>
        <FiltersContainer>
          <SingleLineFilters>
            <SearchContainer>
              <SearchIcon>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </SearchIcon>
              <SearchInput
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchContainer>
            
            <CategoryFilters>
              {categories.map(category => (
                <CategoryButton
                  key={category}
                  active={selectedCategory === category}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </CategoryButton>
              ))}
            </CategoryFilters>
            
            <SortContainer>
              <SortSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </SortSelect>
            </SortContainer>
          </SingleLineFilters>
        </FiltersContainer>
      </FiltersSection>

      {/* Products Section */}
      <ProductsSection ref={productsRef}>
        <ProductsContainer>
          <ProductsHeader>
            <ResultsCount>
              {filteredProducts.length} {filteredProducts.length === 1 ? 'Product' : 'Products'} Found
            </ResultsCount>
          </ProductsHeader>

          <ProductsGrid>
            {filteredProducts.map(product => (
              <ProductCard 
                key={product.id} 
                onClick={() => openModal(product)}
              >
                <ProductBadge>{product.badge}</ProductBadge>
                <ProductImageContainer>
                  <ProductImage src={product.image} alt={product.name} />
                  <ProductOverlay>
                    <QuickViewButton onClick={(e) => {
                      e.stopPropagation();
                      openModal(product);
                    }}>
                      Quick View
                    </QuickViewButton>
                  </ProductOverlay>
                </ProductImageContainer>
                
                <ProductInfo>
                  <ProductCategory>{product.category}</ProductCategory>
                  <ProductName>{product.name}</ProductName>
                  <ProductDescription>
                    {product.description}
                  </ProductDescription>
                  <ProductRating>
                    <Stars rating={product.rating} />
                    <RatingText>({product.reviews})</RatingText>
                  </ProductRating>
                  <ProductPrice>{formatINR(product.price)}</ProductPrice>
                  
                  <ProductActions>
                    <AddToCartButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCartAction(product);
                      }}
                      isInCart={productsInCart.has(product.id)}
                    >
                      {productsInCart.has(product.id) ? (
                        <>
                          <CartIconSmall>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="9" cy="21" r="1"></circle>
                              <circle cx="20" cy="21" r="1"></circle>
                              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                            </svg>
                          </CartIconSmall>
                          Go to Cart
                        </>
                      ) : (
                        'Add to Cart'
                      )}
                    </AddToCartButton>
                    <WishlistButton 
                      onClick={(e) => handleWishlistToggle(product, e)}
                      $isLiked={isItemInWishlist(product.id)}
                    >
                      {isItemInWishlist(product.id) ? '♥' : '♡'}
                    </WishlistButton>
                  </ProductActions>
                </ProductInfo>
              </ProductCard>
            ))}
          </ProductsGrid>

          {filteredProducts.length === 0 && (
            <NoResults>
              <NoResultsIcon>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </NoResultsIcon>
              <NoResultsText>No products found</NoResultsText>
              <NoResultsSubtext>Try adjusting your search or filters</NoResultsSubtext>
            </NoResults>
          )}
        </ProductsContainer>
      </ProductsSection>

      {/* Product Modal */}
      <ProductModal
        isOpen={isModalOpen}
        product={selectedProduct}
        onClose={closeModal}
        onAddToCart={handleAddToCart}
      />
    </StoreContainer>
  );
};

// Helper component for star ratings
const Stars = ({ rating }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;
  
  return (
    <StarsContainer>
      {[...Array(5)].map((_, i) => (
        <Star key={i} filled={i < fullStars || (i === fullStars && hasHalfStar)}>
          ★
        </Star>
      ))}
      <RatingNumber>{rating}</RatingNumber>
    </StarsContainer>
  );
};

export default Store;

// Styled Components
const StoreContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;

const FiltersSection = styled.section`
  background: white;
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: ${props => props.isVisible ? '80px' : '-100px'};
  z-index: 30;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding-top: 0;
  transition: top 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 768px) {
    top: ${props => props.isVisible ? '70px' : '-100px'};
  }
`;

const FiltersContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 20px;
`;

const SingleLineFilters = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 1024px) {
    flex-wrap: wrap;
    gap: 16px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    color: #666;
    transition: color 0.2s ease;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  min-width: 280px;
  flex: 1;
  max-width: 350px;
  
  &:hover ${SearchIcon} svg {
    color: #333;
  }
  
  &:focus-within ${SearchIcon} svg {
    color: #111;
  }
  
  @media (max-width: 768px) {
    min-width: auto;
    max-width: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px 10px 36px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #111;
    box-shadow: 0 0 0 2px rgba(17, 17, 17, 0.05);
  }
  
  &:hover {
    border-color: #ccc;
  }
  
  &::placeholder {
    color: #aaa;
  }
`;

const SortContainer = styled.div`
  min-width: 180px;
  
  @media (max-width: 768px) {
    min-width: auto;
  }
`;

const SortSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #111;
  }
`;







const CategoryFilters = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  
  @media (max-width: 1024px) {
    flex-wrap: wrap;
    gap: 4px;
  }
  
  @media (max-width: 768px) {
    justify-content: center;
    gap: 6px;
  }
`;

const CategoryButton = styled.button`
  padding: 8px 12px;
  border: 1px solid ${props => props.active ? '#111' : '#e0e0e0'};
  background: ${props => props.active ? '#111' : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    border-color: #111;
    background: ${props => props.active ? '#111' : '#f8f8f8'};
  }
  
  @media (max-width: 1024px) {
    padding: 6px 10px;
    font-size: 11px;
  }
`;



const ProductsSection = styled.section`
  padding: 32px 20px;
  background: white;
`;

const ProductsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const ProductsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ResultsCount = styled.h2`
  font-size: 16px;
  font-weight: 500;
  color: #666;
  margin: 0;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #f0f0f0;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  display: block;
  
  &:hover {
    border-color: #e0e0e0;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  }
`;

const ProductBadge = styled.span`
  position: absolute;
  top: 8px;
  left: 8px;
  background: #111;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.3px;
  z-index: 2;
  text-transform: uppercase;
`;

const ProductImageContainer = styled.div`
  position: relative;
  background: #fafafa;
  height: 240px;
  width: 100%;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProductImage = styled.img`
  width: 85%;
  height: 85%;
  object-fit: contain;
  transition: transform 0.2s ease;
  
  ${ProductCard}:hover & {
    transform: scale(1.02);
  }
`;

const ProductOverlay = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
  
  ${ProductCard}:hover & {
    opacity: 1;
  }
`;

const QuickViewButton = styled.button`
  background: white;
  border: 1px solid #e0e0e0;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #f8f8f8;
    border-color: #d0d0d0;
  }
`;

const ProductInfo = styled.div`
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProductCategory = styled.span`
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
`;

const ProductName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #111;
  line-height: 1.3;
`;

const ProductDescription = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ProductRating = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StarsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
`;

const Star = styled.span`
  color: ${props => props.filled ? '#ffc107' : '#e0e0e0'};
  font-size: 12px;
`;

const RatingNumber = styled.span`
  font-size: 11px;
  color: #666;
  font-weight: 500;
`;

const RatingText = styled.span`
  font-size: 11px;
  color: #888;
`;

const ProductPrice = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #111;
  margin-top: 4px;
`;

const ProductActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
`;

const AddToCartButton = styled.button`
  flex: 1;
  background: ${props => props.isInCart ? '#2980b9' : '#111'};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  
  &:hover {
    background: ${props => props.isInCart ? '#1f5582' : '#000'};
  }
`;

const CartIconSmall = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    transition: transform 0.3s ease;
  }
`;

const WishlistButton = styled.button`
  background: ${props => props.$isLiked ? '#ff4757' : 'white'};
  border: 1px solid ${props => props.$isLiked ? '#ff4757' : '#e0e0e0'};
  color: ${props => props.$isLiked ? 'white' : '#666'};
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s ease;
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    border-color: #ff4757;
    color: ${props => props.$isLiked ? 'white' : '#ff4757'};
    background: ${props => props.$isLiked ? '#e63946' : '#fff5f5'};
    transform: translateY(-1px);
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #666;
`;

const NoResultsIcon = styled.div`
  margin-bottom: 20px;
  opacity: 0.5;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    color: #999;
  }
`;

const NoResultsText = styled.h3`
  font-size: 24px;
  margin: 0 0 8px 0;
  color: #333;
`;

const NoResultsSubtext = styled.p`
  font-size: 16px;
  margin: 0;
  opacity: 0.7;
`;


